import React, { useEffect, useRef, useState } from 'react';
import { Check, Loader2, Upload, Download, RefreshCw, X, FileText, AlertCircle, Sparkles } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  CATEGORIAS, JORNADA_DEFAULT, guardarDatos, subirArchivo, quitarArchivo, reintentarCronograma, problemaArchivo, tamano, errorDatos, extension,
} from './datosObra.api';
import { formatDateTime24 } from '../utils/formatTime24';

// Obra › Datos de la obra: jornada laboral (días y horario) y cronograma de obra (Gantt / MS Project).
// La jornada la usa la IA y el clima para saber qué días y horas se trabaja. El cronograma se lee UNA vez al
// subirlo (backend: datosObra/services/cronograma.ts) y Obra › Clima recomienda días según lo que toca hacer.

const DIAS = [[1, 'Lun'], [2, 'Mar'], [3, 'Mié'], [4, 'Jue'], [5, 'Vie'], [6, 'Sáb'], [7, 'Dom']];
const NOMBRE_DIA = { 1: 'lunes', 2: 'martes', 3: 'miércoles', 4: 'jueves', 5: 'viernes', 6: 'sábado', 7: 'domingo' };
const igual = (a, b) => JSON.stringify(a) === JSON.stringify(b);
const fecha = (iso) => { try { return formatDateTime24(iso, { dateOptions: { day: 'numeric', month: 'long', year: 'numeric' } }); } catch (e) { return ''; } };
const dm = (f) => `${Number(f.slice(8, 10))}/${Number(f.slice(5, 7))}`;

function Hora({ id, label, value, onChange }) {
  return (
    <label htmlFor={id} className="flex flex-col gap-1 text-sm font-semibold text-gray-700">
      {label}
      <input id={id} type="time" value={value} onChange={(e) => onChange(e.target.value)} step={900} className="border border-gray-300 rounded-lg px-3 text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" style={{ height: 42 }} />
    </label>
  );
}

/** Días laborales (chips) + horario desde/hasta, con horario aparte opcional para el sábado. */
export function JornadaLaboral({ feedId, ficha, onFicha }) {
  const guardada = ficha?.jornada || null;
  const base = guardada || ficha?.jornadaDefault || JORNADA_DEFAULT;
  const [j, setJ] = useState(base);
  const [sabadoAparte, setSabadoAparte] = useState(!!base.sabado);
  const [guardando, setGuardando] = useState(false);
  useEffect(() => { setJ(base); setSabadoAparte(!!base.sabado); }, [JSON.stringify(base)]); // eslint-disable-line react-hooks/exhaustive-deps

  const conSabado = j.dias.includes(6);
  const armada = { dias: j.dias, desde: j.desde, hasta: j.hasta, sabado: conSabado && sabadoAparte ? (j.sabado || { desde: '08:00', hasta: '13:00' }) : null };
  const cambio = !guardada || !igual(armada, guardada);
  const problema = !j.dias.length ? 'Elegí al menos un día.' : !(j.hasta > j.desde) ? 'El horario tiene que terminar después de empezar.'
    : armada.sabado && !(armada.sabado.hasta > armada.sabado.desde) ? 'El horario del sábado tiene que terminar después de empezar.' : '';
  const toggle = (d) => setJ((x) => ({ ...x, dias: x.dias.includes(d) ? x.dias.filter((y) => y !== d) : [...x.dias, d].sort((a, b) => a - b) }));
  const guardar = async () => {
    setGuardando(true);
    try { onFicha(await guardarDatos(feedId, { jornada: armada })); toast.success('Guardamos la jornada laboral'); } catch (e) { toast.error(errorDatos(e)); }
    setGuardando(false);
  };
  const listaDias = (ds) => {
    if (ds.length === 7) return 'todos los días';
    const corrido = ds.length > 2 && ds.every((d, i) => i === 0 || d === ds[i - 1] + 1);
    if (corrido) return `${NOMBRE_DIA[ds[0]]} a ${NOMBRE_DIA[ds[ds.length - 1]]}`;
    return ds.length > 1 ? `${ds.slice(0, -1).map((d) => NOMBRE_DIA[d]).join(', ')} y ${NOMBRE_DIA[ds[ds.length - 1]]}` : NOMBRE_DIA[ds[0]] || '';
  };
  const resumen = armada.sabado
    ? `${listaDias(j.dias.filter((d) => d !== 6))} de ${j.desde} a ${j.hasta} y sábado de ${armada.sabado.desde} a ${armada.sabado.hasta}`
    : `${listaDias(j.dias)} de ${j.desde} a ${j.hasta}`;
  const esDefault = igual(armada, ficha?.jornadaDefault || JORNADA_DEFAULT);

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm font-semibold text-gray-700 mb-1.5" id="do-dias-label">Días laborales</p>
        <div className="flex flex-wrap gap-1.5" role="group" aria-labelledby="do-dias-label">
          {DIAS.map(([d, t]) => {
            const on = j.dias.includes(d);
            return (
              <button key={d} type="button" aria-pressed={on} onClick={() => toggle(d)}
                className={`min-w-[52px] px-3 rounded-full text-sm font-semibold border transition-colors active:scale-95 ${on ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-gray-300 text-gray-600 hover:border-blue-400'}`} style={{ height: 38 }}>
                {on && <Check size={13} className="inline -mt-0.5 mr-0.5" aria-hidden="true" />}{t}
              </button>
            );
          })}
        </div>
      </div>
      <div className="grid grid-cols-2 sm:max-w-sm gap-3">
        <Hora id="do-jornada-desde" label="Horario desde" value={j.desde} onChange={(v) => setJ((x) => ({ ...x, desde: v }))} />
        <Hora id="do-jornada-hasta" label="Hasta" value={j.hasta} onChange={(v) => setJ((x) => ({ ...x, hasta: v }))} />
      </div>
      {conSabado && (
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 space-y-3 sm:max-w-md">
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={sabadoAparte} onChange={(e) => { setSabadoAparte(e.target.checked); if (e.target.checked && !j.sabado) setJ((x) => ({ ...x, sabado: { desde: '08:00', hasta: '13:00' } })); }} className="w-4 h-4 accent-blue-600" />
            El sábado tiene otro horario
          </label>
          {sabadoAparte && (
            <div className="grid grid-cols-2 gap-3">
              <Hora id="do-sabado-desde" label="Sábado desde" value={armada.sabado?.desde || '08:00'} onChange={(v) => setJ((x) => ({ ...x, sabado: { ...(x.sabado || { hasta: '13:00' }), desde: v } }))} />
              <Hora id="do-sabado-hasta" label="Hasta" value={armada.sabado?.hasta || '13:00'} onChange={(v) => setJ((x) => ({ ...x, sabado: { ...(x.sabado || { desde: '08:00' }), hasta: v } }))} />
            </div>
          )}
        </div>
      )}
      <p className="text-xs text-gray-500">{problema ? <span className="text-rose-700 font-semibold">{problema}</span> : <>Se trabaja {resumen}.{!guardada && esDefault && ' Es el horario por defecto: guardalo si es el de tu obra.'}</>}</p>
      {cambio && (
        <div className="flex justify-end">
          <button type="button" onClick={guardar} disabled={guardando || !!problema} className="inline-flex items-center gap-2 px-4 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-60" style={{ height: 40 }}>
            {guardando ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}Guardar
          </button>
        </div>
      )}
    </div>
  );
}

const ESTADO = {
  listo: { cls: 'border-emerald-200 bg-emerald-50 text-emerald-900' },
  'no-legible': { cls: 'border-amber-200 bg-amber-50 text-amber-900' },
  error: { cls: 'border-rose-200 bg-rose-50 text-rose-900' },
  'sin-presupuesto': { cls: 'border-amber-200 bg-amber-50 text-amber-900' },
};
const FUENTE = { 'ms-project-xml': 'MS Project (XML)', csv: 'CSV', xlsx: 'Excel', 'claude-pdf': 'PDF, leído con IA', 'claude-imagen': 'imagen, leída con IA' };

/** Cronograma de obra (opcional): un solo archivo, con nombre, fecha, descargar, reemplazar y quitar. */
export function CronogramaObra({ feedId, ficha, onFicha }) {
  const archivo = ficha?.archivos?.cronograma?.[0] || null;
  const crono = ficha?.cronograma && archivo && ficha.cronograma.archivoId === archivo.id ? ficha.cronograma : null;
  const tipos = ficha?.tiposActividad || {};
  const input = useRef(null);
  const [subiendo, setSubiendo] = useState(null); // { nombre, pct }
  const [ocupado, setOcupado] = useState('');
  const [encima, setEncima] = useState(false);
  const [verTodas, setVerTodas] = useState(false);
  const c = CATEGORIAS.cronograma;

  const tomar = async (lista) => {
    const f = Array.from(lista || [])[0];
    if (!f) return;
    const p = problemaArchivo('cronograma', f);
    if (p) { toast.error(p); return; }
    setSubiendo({ nombre: f.name, pct: 0 });
    try {
      const nueva = await subirArchivo(feedId, 'cronograma', f, { onProgress: (b) => setSubiendo((s) => s && { ...s, pct: Math.min(100, Math.round((b / Math.max(1, f.size)) * 100)) }) });
      onFicha(nueva);
      const e = nueva?.cronograma?.estado;
      if (e === 'listo') toast.success(`Leímos ${nueva.cronograma.actividades.length} actividades del cronograma`);
      else toast.info('Guardamos el archivo. Mirá el aviso del cronograma.');
    } catch (e) {
      toast.error(`No pudimos subir "${f.name}": ${errorDatos(e)}`);
    }
    setSubiendo(null);
  };
  const quitar = async () => {
    setOcupado('quitar');
    try { onFicha(await quitarArchivo(feedId, archivo.id)); } catch (e) { toast.error(errorDatos(e)); }
    setOcupado('');
  };
  const reintentar = async () => {
    setOcupado('reintentar');
    try { onFicha(await reintentarCronograma(feedId)); } catch (e) { toast.error(errorDatos(e)); }
    setOcupado('');
  };

  const hoy = new Date(Date.now() - 3 * 3600e3).toISOString().slice(0, 10);
  const acts = crono?.actividades || [];
  const proximas = acts.filter((a) => a.fin >= hoy);
  const mostrar = verTodas ? acts : (proximas.length ? proximas : acts).slice(0, 8);
  const elegir = <input ref={input} type="file" className="sr-only" accept={c.accept} onChange={(e) => { tomar(e.target.files); e.target.value = ''; }} />;

  if (subiendo) {
    return (
      <div className="rounded-xl border-2 border-dashed border-blue-300 bg-blue-50/40 px-4 py-5 text-center space-y-1.5" role="status">
        <div className="text-sm text-gray-700 inline-flex items-center gap-2"><Loader2 size={15} className="animate-spin text-blue-600" />
          {subiendo.pct < 100 ? <>Subiendo {subiendo.nombre}… {subiendo.pct}%</> : <>Leyendo el cronograma… puede tardar unos segundos</>}
        </div>
        <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden max-w-xs mx-auto"><div className="h-full bg-blue-600 transition-all" style={{ width: `${subiendo.pct}%` }} /></div>
      </div>
    );
  }

  if (!archivo) {
    return (
      <div
        onDragOver={(e) => { e.preventDefault(); setEncima(true); }} onDragLeave={() => setEncima(false)}
        onDrop={(e) => { e.preventDefault(); setEncima(false); tomar(e.dataTransfer.files); }}
        onClick={() => input.current && input.current.click()} role="button" tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); input.current && input.current.click(); } }}
        className={`rounded-xl border-2 border-dashed px-4 py-5 text-center cursor-pointer transition ${encima ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'}`}>
        <div className="text-sm font-medium text-blue-700 inline-flex items-center gap-1.5"><Upload size={15} />Subí tu Gantt o el archivo de MS Project</div>
        <div className="text-xs text-gray-500 mt-0.5">MPP, XML de Project, PDF, Excel (XLSX/XLS), CSV o imagen · hasta {Math.round(c.max / 1048576)} MB</div>
        {elegir}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-gray-200 bg-white p-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="w-11 h-11 rounded-lg bg-gray-100 text-gray-500 flex flex-col items-center justify-center shrink-0"><FileText size={18} /><span className="text-[9px] font-bold uppercase">{extension(archivo.nombre)}</span></div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-800 truncate" title={archivo.nombre}>{archivo.nombre}</p>
            <p className="text-xs text-gray-500">{archivo.bytes ? `${tamano(archivo.bytes)} · ` : ''}Subido el {fecha(archivo.subidoAt)}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <a href={archivo.url} target="_blank" rel="noopener noreferrer" download={archivo.nombre} className="inline-flex items-center gap-1.5 px-3 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50" style={{ height: 36 }}><Download size={14} />Descargar</a>
          <button type="button" onClick={() => input.current && input.current.click()} disabled={!!ocupado} className="inline-flex items-center gap-1.5 px-3 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50" style={{ height: 36 }}><Upload size={14} />Reemplazar</button>
          <button type="button" onClick={quitar} disabled={!!ocupado} aria-label={`Quitar ${archivo.nombre}`} className="inline-flex items-center gap-1.5 px-3 rounded-lg border border-gray-300 text-sm font-medium text-gray-600 hover:text-red-600 hover:border-red-300 disabled:opacity-50" style={{ height: 36 }}>{ocupado === 'quitar' ? <Loader2 size={14} className="animate-spin" /> : <X size={14} />}Quitar</button>
        </div>
        {elegir}
      </div>

      {!crono && (
        <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700 flex flex-wrap items-center justify-between gap-2">
          Todavía no leímos este cronograma.
          <button type="button" onClick={reintentar} disabled={!!ocupado} className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 hover:underline disabled:opacity-50">{ocupado === 'reintentar' ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}Leerlo ahora</button>
        </div>
      )}
      {crono && crono.estado !== 'listo' && (
        <div className={`rounded-xl border px-3 py-2.5 text-sm flex gap-2 ${ESTADO[crono.estado]?.cls || ESTADO.error.cls}`}>
          <AlertCircle size={16} className="shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <p>{crono.mensaje || 'No pudimos leer el cronograma.'}</p>
            {(crono.estado === 'error' || crono.estado === 'sin-presupuesto') && (
              <button type="button" onClick={reintentar} disabled={!!ocupado} className="mt-1 inline-flex items-center gap-1.5 text-sm font-semibold underline disabled:opacity-50">{ocupado === 'reintentar' ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}Probar de nuevo</button>
            )}
          </div>
        </div>
      )}
      {crono && crono.estado === 'listo' && (
        <div className="rounded-xl border border-emerald-200 bg-white overflow-hidden">
          <div className="px-3 py-2.5 bg-emerald-50 text-sm text-emerald-900 flex flex-wrap items-center justify-between gap-2">
            <span className="inline-flex items-center gap-1.5"><Sparkles size={15} />Leímos <b>{acts.length}</b> actividades ({FUENTE[crono.fuente] || 'archivo'}). El Clima te avisa qué días conviene cada una.</span>
          </div>
          <ul className="divide-y divide-gray-100">
            {mostrar.map((a) => (
              <li key={`${a.nombre}-${a.inicio}-${a.fin}`} className="px-3 py-2 flex items-center gap-3 text-sm">
                <span className="w-24 shrink-0 text-xs font-semibold text-gray-600 tabular-nums">{a.inicio === a.fin ? dm(a.inicio) : `${dm(a.inicio)} – ${dm(a.fin)}`}</span>
                <span className="flex-1 min-w-0 truncate text-gray-800" title={a.nombre}>{a.nombre}</span>
                <span className={`hidden sm:inline shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full ${a.tipo === 'interior' ? 'bg-gray-100 text-gray-600' : 'bg-blue-50 text-blue-700'}`}>{tipos[a.tipo] || a.tipo}</span>
              </li>
            ))}
          </ul>
          {acts.length > mostrar.length || verTodas ? (
            <button type="button" onClick={() => setVerTodas((v) => !v)} className="w-full px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-gray-50 border-t border-gray-100">{verTodas ? 'Ver menos' : `Ver las ${acts.length} actividades`}</button>
          ) : null}
        </div>
      )}
    </div>
  );
}
